import { Controller } from "stimulus"

// Filters for /testimonials (pages/testimonials.html). Each card carries its
// categories as data attributes (data-databases="postgres mysql", ...; see
// testimonial-cards.html with filters=true), and the url says what to keep:
// ?databases=postgres&switched_from=dbeaver keeps the cards with both, ?q=
// the ones whose name, title or quote contains every word, ?rating=5 (or 4,
// or none) the ones with that rating. So a link can point at every
// testimonial on a topic. ?controls=true also shows the filters themselves
// (the page's template), which change the url as they change.
//
// The build lays the cards out in columns (testimonial_columns in
// _plugins/testimonials.rb). With a filter on, the cards left are laid out
// again, in their list order, each under the shortest column; with none,
// every card goes back to the column the build gave it (data-column).
const FIELDS = ['q', 'rating', 'switched_from', 'competitors', 'databases', 'features', 'platforms', 'audience', 'language']

export default class TestimonialFilterController extends Controller {

  static get targets() {
    return ['template', 'wall', 'count', 'empty', 'link']
  }

  connect() {
    const params = new URLSearchParams(window.location.search)
    this.controls = params.get('controls') === 'true'
    this.cards = Array.from(this.wallTarget.querySelectorAll('.tm-card'))
      .sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order))
    this.columns = Array.from(this.wallTarget.children)

    // A page Turbo restores from its cache still has the last visit's filters.
    this.element.querySelector('.tm-filters')?.remove()
    if (this.hasLinkTarget) this.linkTarget.hidden = this.controls
    if (this.controls) {
      this.element.insertBefore(this.templateTarget.content.cloneNode(true), this.wallTarget)
      this.form = this.element.querySelector('.tm-filters')
      FIELDS.forEach((name) => {
        const field = this.form.elements[name]
        if (field) field.value = params.get(name) || ''
      })
    }

    this.state = Object.fromEntries(FIELDS.map((name) => [name, params.get(name) || '']))
    this.apply()
  }

  disconnect() {
    // Turbo's snapshot of the page, and the next visit, start from the build's layout.
    this.state = {}
    this.apply()
  }

  filter() {
    this.state = Object.fromEntries(FIELDS.map((name) => [name, this.form.elements[name]?.value || '']))
    this.apply()
    this.updateUrl()
  }

  clear() {
    this.form.reset()
    this.filter()
  }

  ignore(event) {
    event.preventDefault()
  }

  apply() {
    const words = (this.state.q || '').trim().toLowerCase().split(/\s+/).filter(Boolean)
    const active = FIELDS.some((name) => this.state[name])
    const shown = this.cards.filter((card) => {
      const match = this.matches(card, words)
      card.hidden = !match
      return match
    })

    if (active) {
      this.layout(shown)
    } else {
      this.cards.forEach((card) => this.columns[Number(card.dataset.column)].appendChild(card))
    }

    this.emptyTarget.hidden = shown.length > 0
    if (this.hasCountTarget) {
      this.countTarget.textContent = `Showing ${shown.length} of ${this.cards.length} testimonials`
    }
  }

  matches(card, words) {
    const categories = FIELDS.slice(2).every((name) => {
      const value = this.state[name]
      if (!value) return true
      return (card.dataset[this.datasetKey(name)] || '').split(' ').includes(value)
    })
    if (!categories) return false

    const rating = card.dataset.rating
    if (this.state.rating === 'none' && rating) return false
    if (/^\d$/.test(this.state.rating) && !(Number(rating) >= Number(this.state.rating))) return false

    if (words.length === 0) return true
    const text = card.textContent.toLowerCase()
    return words.every((word) => text.includes(word))
  }

  // switched_from is data-switched-from, card.dataset.switchedFrom.
  datasetKey(name) {
    return name.replace(/_(\w)/g, (_, letter) => letter.toUpperCase())
  }

  // Each card under whichever column is shortest so far, as the build does.
  // On narrower screens the columns step aside and each card's order puts
  // it back in its place, so this does no harm there.
  layout(cards) {
    const heights = this.columns.map(() => 0)
    cards.forEach((card) => {
      const column = heights.indexOf(Math.min(...heights))
      this.columns[column].appendChild(card)
      heights[column] += card.offsetHeight
    })
  }

  updateUrl() {
    const url = new URL(window.location.href)
    FIELDS.forEach((name) => {
      const value = (this.state[name] || '').trim()
      value ? url.searchParams.set(name, value) : url.searchParams.delete(name)
    })
    // Turbo keeps its restoration identifier in history.state: pass it through.
    history.replaceState(history.state, '', url)
  }
}
